import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useAuth } from './lib/auth';
import { isPlatformRole } from './lib/roles';
import { useLegalPagesVisible } from './lib/useLegalPagesVisible';
import type { LegalPageKey } from './lib/types';
import { Spinner } from './components/ui';

/**
 * Route guards, lifted out of the old routes.tsx during the React Router framework-mode migration.
 *
 * They were plain wrapper components (`<RequireAuth><Page/></RequireAuth>`) in the JSX route tree.
 * Framework mode declares routes in a config file with no JSX, so each guard is also exported as a
 * LAYOUT component (`…Layout`) that renders `<Outlet />` — routes.ts nests the guarded routes
 * under it. Behaviour is unchanged; only the wiring moved.
 */

/** Where an authenticated user lands, by role. */
export function homePath(role?: string): string {
  if (isPlatformRole(role)) return '/admin';
  if (role === 'affiliate') return '/affiliate';
  return '/app';
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const status = useAuth((s) => s.status);
  if (status === 'loading') return <Spinner />;
  if (status === 'guest') return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function RequireSuperAdmin({ children }: { children: ReactNode }) {
  const status = useAuth((s) => s.status);
  const role = useAuth((s) => s.user?.role);
  if (status === 'loading') return <Spinner />;
  if (status === 'guest') return <Navigate to="/login" replace />;
  if (!isPlatformRole(role)) return <Navigate to={homePath(role)} replace />;
  return <>{children}</>;
}

/** Tenant + agency portal (`/app`). Super-admins / affiliates go to their own portals. */
export function RequireTenant({ children }: { children: ReactNode }) {
  const status = useAuth((s) => s.status);
  const role = useAuth((s) => s.user?.role);
  if (status === 'loading') return <Spinner />;
  if (status === 'guest') return <Navigate to="/login" replace />;
  if (role !== 'tenant_owner' && role !== 'tenant_member') return <Navigate to={homePath(role)} replace />;
  return <>{children}</>;
}

/** Affiliate portal (`/affiliate`). Everyone else is bounced to their own home. */
export function RequireAffiliate({ children }: { children: ReactNode }) {
  const status = useAuth((s) => s.status);
  const role = useAuth((s) => s.user?.role);
  if (status === 'loading') return <Spinner />;
  if (status === 'guest') return <Navigate to="/login" replace />;
  if (role !== 'affiliate') return <Navigate to={homePath(role)} replace />;
  return <>{children}</>;
}

export function RequireTenantOwner({ children }: { children: ReactNode }) {
  const status = useAuth((s) => s.status);
  const role = useAuth((s) => s.user?.role);
  if (status === 'loading') return <Spinner />;
  if (status === 'guest') return <Navigate to="/login" replace />;
  if (role === 'super_admin') return <Navigate to="/admin" replace />;
  if (role !== 'tenant_owner') return <Navigate to="/app" replace />;
  return <>{children}</>;
}

/** Agency console (`/app/agency`). Only an agency *owner* may enter. */
export function RequireAgency({ children }: { children: ReactNode }) {
  const status = useAuth((s) => s.status);
  const user = useAuth((s) => s.user);
  if (status === 'loading') return <Spinner />;
  if (status === 'guest') return <Navigate to="/login" replace />;
  if (user?.role === 'super_admin') return <Navigate to="/admin" replace />;
  if (user?.role !== 'tenant_owner' || !user?.tenant?.isAgency) {
    return <Navigate to="/app" replace />;
  }
  return <>{children}</>;
}

export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const status = useAuth((s) => s.status);
  const role = useAuth((s) => s.user?.role);
  if (status === 'authed') return <Navigate to={homePath(role)} replace />;
  return <>{children}</>;
}

/** Guards a public legal route with the operator's `/legal/pages` visibility map. */
export function LegalPageGuard({ page, children }: { page: LegalPageKey; children: ReactNode }) {
  const { isVisible } = useLegalPagesVisible();
  if (!isVisible(page)) return <Navigate to="/404" replace />;
  return <>{children}</>;
}

// ── Layout wrappers for routes.ts ────────────────────────────────────────────
// Framework mode nests routes under a layout module rather than wrapping elements in JSX.

export function RequireAuthLayout() {
  return <RequireAuth><Outlet /></RequireAuth>;
}
export function RequireTenantLayout() {
  return <RequireTenant><Outlet /></RequireTenant>;
}
export function RequireTenantOwnerLayout() {
  return <RequireTenantOwner><Outlet /></RequireTenantOwner>;
}
export function RequireAgencyLayout() {
  return <RequireAgency><Outlet /></RequireAgency>;
}
export function RequireSuperAdminLayout() {
  return <RequireSuperAdmin><Outlet /></RequireSuperAdmin>;
}
export function RequireAffiliateLayout() {
  return <RequireAffiliate><Outlet /></RequireAffiliate>;
}
export function RedirectIfAuthedLayout() {
  return <RedirectIfAuthed><Outlet /></RedirectIfAuthed>;
}

/**
 * Legal-visibility guard as a layout. The old table passed the page key per route
 * (`pubLegal(…, 'privacy')`); here it is derived from the pathname, which keeps routes.ts free of
 * per-route wiring and cannot drift out of sync with the URL.
 */
export function LegalPageGuardLayout() {
  const { pathname } = useLocation();
  const key = pathname.replace(/^\/+|\/+$/g, '') as LegalPageKey;
  return <LegalPageGuard page={key}><Outlet /></LegalPageGuard>;
}
